import React, { useState, useEffect, useCallback } from 'react';
import { useJazyk } from '../context/LanguageContext';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, MapPin, Star, Stethoscope, BookOpen,
  Calendar, ChevronRight, Compass,
} from 'lucide-react';

import { API, mediaUrl } from '../lib/api';
import { STAGES, nazevFaze } from '../lib/journeyStages';
import { plural } from '../lib/utils';

export default function JourneyGuidePage() {
  const { t } = useJazyk();
  const { stageId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [stages, setStages] = useState([]);
  const [stage, setStage] = useState(null);
  const [related, setRelated] = useState({ specialists: [], stories: [] });
  const [progress, setProgress] = useState({});     // { stage_id: [item_id, …] }
  const [myJourney, setMyJourney] = useState(null); // moje uložená fáze (kvůli „{t('pruvodce.tadyJsi')}")
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [settingStage, setSettingStage] = useState(false);

  // Pás fází, můj postup a moje uložená fáze – načítáme jednou
  useEffect(() => {
    axios.get(`${API}/journey/stages`).then(r => setStages(r.data)).catch(() => {});
    axios.get(`${API}/journey/progress`).then(r => setProgress(r.data || {})).catch(() => {});
    axios.get(`${API}/users/me`).then(r => setMyJourney(r.data?.journey || null)).catch(() => {});
  }, []);

  // Detail fáze + související obsah – při každé změně fáze v URL
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    Promise.all([
      axios.get(`${API}/journey/stages/${stageId}`),
      axios.get(`${API}/journey/stages/${stageId}/related`),
    ])
      .then(([s, r]) => {
        if (cancelled) return;
        setStage(s.data);
        setRelated(r.data || { specialists: [], stories: [] });
      })
      .catch(() => { if (!cancelled) { setStage(null); setNotFound(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [stageId]);

  const checkedItems = progress[stageId] || [];
  const checklist = stage?.checklist || [];
  const doneCount = checklist.filter(i => checkedItems.includes(i.id)).length;

  // Optimistické odškrtnutí – stav se překlopí hned, server se dorovná na pozadí
  const toggleItem = useCallback(async (itemId) => {
    if (!user) return;
    const wasChecked = (progress[stageId] || []).includes(itemId);
    setProgress(prev => {
      const current = prev[stageId] || [];
      return {
        ...prev,
        [stageId]: wasChecked ? current.filter(i => i !== itemId) : [...current, itemId],
      };
    });
    try {
      await axios.put(`${API}/journey/progress/${stageId}/${itemId}`);
    } catch {
      // vrátit zpět, když se uložení nepovede
      setProgress(prev => {
        const current = prev[stageId] || [];
        return {
          ...prev,
          [stageId]: wasChecked ? [...current, itemId] : current.filter(i => i !== itemId),
        };
      });
      toast.error(t('pruvodce.chyba'));
    }
  }, [progress, stageId, user]);

  const isMyStage = myJourney?.stage === stageId;

  // Nastavení fáze zachová stávající soukromí i poznámku – čtení průvodce
  // samo o sobě nikdy nic nezveřejňuje.
  const setAsMyStage = async () => {
    setSettingStage(true);
    try {
      await axios.put(`${API}/users/me/journey`, {
        stage: stageId,
        stage_label: stage?.name || stageId,
        is_public: myJourney?.is_public || false,
        note: myJourney?.note || '',
      });
      setMyJourney(prev => ({ ...(prev || {}), stage: stageId, stage_label: stage?.name || stageId }));
      toast.success(t('pruvodce.fazeNastavena'));
    } catch { toast.error(t('cesta.chyba')); }
    finally { setSettingStage(false); }
  };

  // Než dorazí data ze serveru, vykreslíme pás z lokální definice fází
  const stripStages = stages.length > 0
    ? stages
    : STAGES.map((s, i) => ({ id: s.id, name: t(s.klic), color: s.color, order: i }));

  if (notFound) return (
    <div className="min-h-screen py-6" data-testid="journey-guide-not-found">
      <div className="max-w-3xl mx-auto px-4 text-center py-16">
        <Compass className="w-10 h-10 text-bloom-sub/30 mx-auto mb-3" />
        <h1 className="font-serif text-xl font-semibold text-bloom-text mb-3">{t('pruvodce.fazeNenalezena')}</h1>
        <Button variant="ghost" className="text-bloom-violet" onClick={() => navigate('/journey')}>
          <ArrowLeft className="w-4 h-4 mr-1" />{t('pruvodce.zpetNaCestu')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-6" data-testid="journey-guide-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">

        <Link to="/journey" className="inline-flex items-center gap-1.5 text-sm text-bloom-sub hover:text-bloom-violet transition-colors" data-testid="back-to-journey">
          <ArrowLeft className="w-4 h-4" />{t('nav.mojeCesta')}
        </Link>

        {/* Pás fází – na mobilu vodorovně rolovatelný */}
        <div className="-mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-2 overflow-x-auto pb-2" data-testid="journey-stage-strip">
            {stripStages.map(s => {
              const active = s.id === stageId;
              return (
                <button
                  key={s.id}
                  onClick={() => navigate(`/journey/${s.id}`)}
                  className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition-all whitespace-nowrap ${
                    active
                      ? 'border-bloom-violet/60 bg-bloom-violet/5 text-bloom-violet shadow-sm'
                      : 'border-border/50 bg-white text-bloom-sub hover:border-bloom-violet/30 hover:text-bloom-violet'
                  }`}
                  data-testid={`strip-stage-${s.id}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                  {nazevFaze(t, s.id, s.name)}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : stage && (
          <>
            {/* Hlavička s barvou fáze */}
            <Card className="bg-white border-border/50 overflow-hidden" data-testid="journey-stage-header">
              <div className="h-2" style={{ background: stage.color }} />
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h1 className="font-serif text-2xl font-bold text-bloom-text">{nazevFaze(t, stageId, stage.name)}</h1>
                      {isMyStage && (
                        <span className="px-2 py-0.5 rounded-full bg-bloom-violet/10 text-bloom-violet text-[11px] font-semibold" data-testid="you-are-here-badge">
                          {t('pruvodce.tadyJsi')}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-bloom-sub leading-relaxed">{stage.short_description}</p>
                  </div>
                  {!isMyStage && (
                    <Button size="sm" variant="outline" className="border-bloom-violet/30 text-bloom-violet shrink-0"
                      onClick={setAsMyStage} disabled={settingStage} data-testid="set-as-my-stage-btn">
                      {settingStage ? t('pf.ukladam') : t('pruvodce.nastavitFazi')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Text průvodce */}
            <Card className="bg-white border-border/50">
              <CardContent className="p-5">
                {stage.content ? (
                  <div className="article-content text-sm md:text-base text-bloom-sub leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: stage.content }} data-testid="journey-stage-content" />
                ) : (
                  <div className="text-center py-8" data-testid="journey-stage-content-empty">
                    <BookOpen className="w-8 h-8 text-bloom-sub/30 mx-auto mb-2" />
                    <p className="text-sm text-bloom-sub">{t('pruvodce.pripravujeme')}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Checklist */}
            {checklist.length > 0 && (
              <Card className="bg-white border-border/50" data-testid="journey-checklist">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <h2 className="font-serif text-lg font-semibold text-bloom-text">
                      {checklist.length} {plural(checklist.length, 'krok', 'kroky', t('pruvodce.kroku'))}
                    </h2>
                    <span className="text-xs text-bloom-sub" data-testid="journey-progress-label">
                      {doneCount} z {checklist.length} hotovo
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                      style={{ width: `${checklist.length ? (doneCount / checklist.length) * 100 : 0}%`, background: stage.color }} />
                  </div>
                  <div className="space-y-1.5 pt-1">
                    {checklist.map(item => {
                      const checked = checkedItems.includes(item.id);
                      return (
                        <button key={item.id} type="button" onClick={() => toggleItem(item.id)} disabled={!user}
                          className={`w-full flex items-start gap-3 p-2.5 rounded-lg border text-left transition-all ${
                            checked ? 'border-bloom-violet/30 bg-bloom-violet/5' : 'border-border/50 bg-white hover:border-bloom-violet/30'
                          } ${!user ? 'cursor-default opacity-70' : ''}`}
                          data-testid={`checklist-item-${item.id}`}>
                          <span className={`w-4 h-4 mt-0.5 rounded border flex items-center justify-center shrink-0 ${
                            checked ? 'bg-bloom-violet border-bloom-violet' : 'border-border bg-white'
                          }`}>
                            {checked && <Check className="w-3 h-3 text-white" />}
                          </span>
                          <span className={`text-sm leading-relaxed ${checked ? 'text-bloom-sub line-through' : 'text-bloom-text'}`}>
                            {item.text}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-bloom-sub/70 pt-1">{t('pruvodce.krokyJenTy')}</p>
                </CardContent>
              </Card>
            )}

            {/* Odborníci pro tuhle fázi */}
            {related.specialists?.length > 0 && (
              <div className="space-y-2" data-testid="journey-related-specialists">
                <h2 className="font-serif text-lg font-semibold text-bloom-text flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-bloom-pride-blue" />
                  {related.specialists.length} {t('pruvodce.odborniku', { pocet: related.specialists.length })}
                </h2>
                {related.specialists.map(s => (
                  <Link key={s.id} to="/specialists" data-testid={`journey-specialist-${s.id}`}>
                    <Card className="bg-white border-border/50 card-hover">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-bloom-pride-blue/10 flex items-center justify-center shrink-0">
                          <Stethoscope className="w-4 h-4 text-bloom-pride-blue" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-bloom-text truncate">{s.name}</p>
                          <p className="text-xs text-bloom-violet truncate">{s.specialty}</p>
                          <p className="text-xs text-bloom-sub flex items-center gap-1">
                            <MapPin className="w-3 h-3" />{s.city}{s.region ? `, ${s.region}` : ''}
                          </p>
                        </div>
                        {s.review_count > 0 && (
                          <div className="flex items-center gap-1 shrink-0">
                            <Star className="w-3.5 h-3.5 text-bloom-pride-pink fill-bloom-pride-pink" />
                            <span className="text-sm text-bloom-text">{s.avg_rating}</span>
                          </div>
                        )}
                        <ChevronRight className="w-4 h-4 text-bloom-sub/50 shrink-0" />
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}

            {/* Příběhy lidí v téhle fázi */}
            {related.stories?.length > 0 && (
              <div className="space-y-2" data-testid="journey-related-stories">
                <h2 className="font-serif text-lg font-semibold text-bloom-text flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-bloom-pride-pink" />
                  {related.stories.length} {t('pruvodce.pribehu', { pocet: related.stories.length })}
                </h2>
                {related.stories.map(story => (
                  <Link key={story.id} to={`/zkusenosti/${story.id}`} data-testid={`journey-story-${story.id}`}>
                    <Card className="bg-white border-border/50 card-hover">
                      <CardContent className="p-4 flex items-center gap-3">
                        {story.image_url && (
                          <img src={mediaUrl(story.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0"
                            onError={e => { e.currentTarget.style.display = 'none'; }} />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-bloom-text line-clamp-2">{story.title}</p>
                          <p className="text-xs text-bloom-sub flex items-center gap-1 mt-0.5">
                            <Calendar className="w-3 h-3" />
                            {new Date(story.created_at).toLocaleDateString('cs-CZ')}
                            {(story.author_name || story.admin_name) && <> · {story.author_name || story.admin_name}</>}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-bloom-sub/50 shrink-0" />
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
